/**
 * 업무 프로젝트 캘린더 보기 — 월 달력에 업무를 시작일~마감일 막대로(색 = 유형 색 · 잔디와 같다).
 * 칸 계산·줄 배치는 경영지원팀 보드의 순수 함수를 빌려 쓰고, 그리기는 여기서 한다(막대를 누르면 업무 상세).
 * 이 보기는 리스트의 200건 목록과 따로, 보이는 6주 범위만 서버에 묻는다.
 *
 * - 🔴 CalendarGrid.jsx 는 건드리지 않는다(경영지원팀 보드 화면은 한 글자도 안 바뀐다) — 순수 함수
 *   `buildMonthCells`·`layoutSchedulesForMonth` 둘만 빌려 쓴다.
 * - 🔴 제목 칸 = '보이는 첫 칸' 이다 — 지난달에 시작한 업무도 42칸의 첫 칸에서 제목이 보인다
 *   (CalendarGrid 의 showTitle 은 '시작 칸' 이라 그런 막대는 제목이 어디에도 안 나온다).
 * - 🔴 제목 조각만 버튼이다 · 이어지는 조각은 aria-hidden 이고 포커스 가능한 요소를 두지 않는다.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { buildMonthCells, layoutSchedulesForMonth } from '../supportboard/CalendarGrid';
import { errorText } from './errorText';
import { taskPmAPI } from '../../api/client';
import { COLORS, RADIUS, btn, card } from '../../utils/uiStyles';

const WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];
// 막대 치수는 경영지원팀 보드 달력과 같다(머리 숫자 아래 30 에서 시작 · 줄 높이 23 · 막대 19).
const BAR_TOP = 30;
const LANE_HEIGHT = 23;
// 보이는 6주만 묻지만 그 안에 업무가 많을 수 있다 → 서버 상한(500)까지 받는다.
const CALENDAR_LIMIT = 500;

// 🔴 오늘은 브라우저 '로컬' 날짜로 만든다 — toISOString() 은 KST 0~9시에 하루 어긋난다.
const pad2 = (n) => String(n).padStart(2, '0');
function localTodayKey() {
  const now = new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
}
// 'YYYY-MM-DD' → { year, month(0-11) }
const monthOf = (key) => ({ year: Number(String(key).slice(0, 4)), month: Number(String(key).slice(5, 7)) - 1 });

export default function TaskPmCalendar({
  projectId, quick, q, refreshKey, optionMap, onOpenTask, todayKey = localTodayKey(),
}) {
  const [ym, setYm] = useState(() => monthOf(todayKey));
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  // 늦게 온 응답이 최신 달을 덮지 않게 요청마다 번호를 매긴다.
  const seqRef = useRef(0);

  const cells = useMemo(() => buildMonthCells(ym.year, ym.month), [ym]);
  const from = cells[0].date;
  const to = cells[cells.length - 1].date;

  // ── 보이는 42칸 범위만 묻는다(서버는 coalesce 로 '기간 겹침' 을 본다).
  useEffect(() => {
    if (!projectId) return;
    const seq = seqRef.current + 1;
    seqRef.current = seq;
    const params = { sort: 'number', limit: CALENDAR_LIMIT, date_from: from, date_to: to };
    if (quick) params.quick = quick;
    if (q && q.trim()) params.q = q.trim();
    // 🔴 보낼 때 오류를 비운다 — 한 번 실패한 문구가 다음 성공 뒤에 남지 않게.
    setError('');
    taskPmAPI.listTasks(projectId, params)
      .then((res) => {
        if (seqRef.current !== seq) return;          // 지나간 요청의 응답은 버린다
        setItems(Array.isArray(res?.data?.items) ? res.data.items : []);
        setTotal(Number(res?.data?.total || 0));
      })
      .catch((e) => {
        if (seqRef.current !== seq) return;
        // 🔴 실패하면 막대·안내를 비운다 — 옛 total 로 «처음 N개만» 이 같이 뜨지 않게.
        setItems([]);
        setTotal(0);
        setError(errorText(e, '캘린더 업무를 불러오지 못했습니다.'));
      });
  }, [projectId, quick, q, from, to, refreshKey]);

  // 시작·마감 중 하나만 있으면 그 날짜 하루짜리 막대다(둘 다 없는 업무는 서버가 빼므로 여기 없다).
  const schedules = useMemo(() => items.map((t) => ({
    id: t.id,
    key: t.key,
    title: t.title,
    start_date: t.start_date || t.due_date,
    end_date: t.due_date || t.start_date,
    done: Boolean(t.is_done),
    type_id: t.type_id,
  })), [items]);

  const laidOutCells = useMemo(() => layoutSchedulesForMonth(schedules, cells), [schedules, cells]);
  const laneCount = laidOutCells.reduce(
    (max, cell) => cell.items.reduce((cellMax, item) => Math.max(cellMax, item.lane + 1), max),
    0,
  );
  const cellHeight = Math.max(94, BAR_TOP + laneCount * LANE_HEIGHT + 8);

  // 제목을 그리는 칸 — 보이는 범위 앞에서 시작한 막대는 첫 칸이 제목 칸이다.
  const titleCellOf = (schedule) => {
    const key = String(schedule.start_date || '').slice(0, 10);
    return key < from ? from : key;
  };
  // 색 = 유형 색(없으면 정보색). 잔디도 유형 색이 캘린더 막대 색이다.
  const barColors = (schedule) => {
    const option = optionMap && optionMap.get ? optionMap.get(schedule.type_id) : null;
    if (option && option.color) return { background: `${option.color}33`, color: option.color };
    return { background: COLORS.infoSoft, color: COLORS.info };
  };

  const shiftMonth = (delta) => setYm((prev) => {
    const d = new Date(prev.year, prev.month + delta, 1, 12);
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  return (
    <section
      aria-label={`${ym.year}년 ${ym.month + 1}월 업무 달력`}
      style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.lg, padding: '16px', overflowX: 'auto' }}
    >
      {/* 머리 줄 — 가운데에 ‹ 달 › 묶음, 오른쪽 끝에 «오늘»(잔디처럼 이전·다음·오늘 셋). */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', marginBottom: '14px' }}>
        <div />
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button type="button" aria-label="이전 달" onClick={() => shiftMonth(-1)} style={btn('secondary', 'sm')}>‹</button>
          <h3 style={{ margin: 0, color: COLORS.text, fontSize: '17px' }}>{ym.year}년 {ym.month + 1}월</h3>
          <button type="button" aria-label="다음 달" onClick={() => shiftMonth(1)} style={btn('secondary', 'sm')}>›</button>
        </div>
        <button type="button" onClick={() => setYm(monthOf(todayKey))}
          style={{ ...btn('secondary', 'sm'), justifySelf: 'end' }}>오늘</button>
      </div>

      <div style={{ minWidth: '700px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
          {WEEKDAY_LABELS.map((label, index) => (
            <div
              key={label}
              style={{
                padding: '7px 4px',
                textAlign: 'center',
                color: index === 0 ? COLORS.dangerText : index === 6 ? COLORS.info : COLORS.textSub,
                fontSize: '12px',
                fontWeight: 700,
              }}
            >
              {label}
            </div>
          ))}
        </div>

        <div
          role="grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            borderTop: `1px solid ${COLORS.border}`,
            borderLeft: `1px solid ${COLORS.border}`,
          }}
        >
          {laidOutCells.map((cell, cellIndex) => {
            const isToday = cell.date === todayKey;
            const weekStart = cellIndex % 7 === 0;
            return (
              <div
                role="gridcell"
                key={cell.date}
                aria-label={cell.date}
                style={{
                  position: 'relative',
                  minWidth: 0,
                  height: `${cellHeight}px`,
                  boxSizing: 'border-box',
                  background: cell.inCurrentMonth ? COLORS.surface : COLORS.bg,
                  borderRight: `1px solid ${COLORS.border}`,
                  borderBottom: `1px solid ${COLORS.border}`,
                  overflow: 'hidden',
                }}
              >
                {/* 오늘은 날짜 숫자를 원으로 감싼다. */}
                <span
                  style={{
                    position: 'absolute',
                    top: isToday ? '4px' : '6px',
                    right: isToday ? '6px' : '8px',
                    width: isToday ? '22px' : undefined,
                    height: isToday ? '22px' : undefined,
                    borderRadius: isToday ? '50%' : undefined,
                    background: isToday ? COLORS.primary : undefined,
                    display: isToday ? 'inline-flex' : undefined,
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isToday
                      ? '#fff'
                      : cell.inCurrentMonth
                        ? (weekStart ? COLORS.dangerText : cellIndex % 7 === 6 ? COLORS.info : COLORS.text)
                        : COLORS.textMuted,
                    fontSize: '12px',
                    fontWeight: isToday || cell.inCurrentMonth ? 700 : 500,
                  }}
                >
                  {cell.day}
                </span>

                {cell.items.map((item) => {
                  const schedule = item.schedule;
                  const isTitleCell = cell.date === titleCellOf(schedule);
                  const label = `${schedule.key} ${schedule.title}`;
                  const leftRadius = item.isSegmentStart ? '4px' : 0;
                  const rightRadius = item.isSegmentEnd ? '4px' : 0;
                  const barStyle = {
                    position: 'absolute',
                    top: `${BAR_TOP + item.lane * LANE_HEIGHT}px`,
                    left: item.isSegmentStart ? '4px' : 0,
                    right: item.isSegmentEnd ? '4px' : 0,
                    height: '19px',
                    boxSizing: 'border-box',
                    borderRadius: `${leftRadius} ${rightRadius} ${rightRadius} ${leftRadius}`,
                    padding: isTitleCell || weekStart ? '2px 6px' : 0,
                    fontSize: '11px',
                    lineHeight: '15px',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    // 완료 업무는 흐리게 + 취소선(리스트·칸반과 같은 뜻).
                    textDecoration: schedule.done ? 'line-through' : 'none',
                    opacity: schedule.done ? 0.55 : 1,
                    ...barColors(schedule),
                  };
                  if (isTitleCell) {
                    return (
                      <button
                        key={`${schedule.id}-${cell.date}`}
                        type="button"
                        aria-label={label}
                        title={label}
                        onClick={() => { if (onOpenTask) onOpenTask(schedule.id); }}
                        style={{ ...barStyle, display: 'block', border: 'none', textAlign: 'left', fontFamily: 'inherit', cursor: 'pointer' }}
                      >
                        {schedule.title}
                      </button>
                    );
                  }
                  // 이어지는 조각 — 주 첫 칸에서만 제목을 한 번 더 보여 주되 포커스는 받지 않는다.
                  return (
                    <div key={`${schedule.id}-${cell.date}`} aria-hidden="true" style={barStyle}>
                      {weekStart ? schedule.title : null}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {error && (
        <div role="alert" style={{ ...card, background: COLORS.dangerSoft, color: COLORS.dangerText, marginTop: '12px' }}>{error}</div>
      )}
      {total > items.length && (
        <p style={{ margin: '8px 0 0', fontSize: '12px', color: COLORS.textSub }}>
          이 달력 범위에서 처음 {items.length}개만 표시합니다(전체 {total}개).
        </p>
      )}
    </section>
  );
}
